// Страницы документации «Настройка веб-хуков» (main WebhookController, WebhookCreateRequest, WebhookUpdateRequest,
// WebhookResource). Методы кабинета: нужен токен входа руководителя.
import type { ApiDocBlock, ApiDocPage } from '../types'

const webhook = (indent: string) => `{
${indent}  "id": 4,
${indent}  "event": "customer.created",
${indent}  "event_name": "Покупатель создан",
${indent}  "method": "POST",
${indent}  "url": "https://crm.example.com/ifb/hook",
${indent}  "basic_auth_login": "crm",
${indent}  "basic_auth_password": "Установлен",
${indent}  "headers": {
${indent}    "X-Source": "ifb"
${indent}  },
${indent}  "is_active": true
${indent}}`

const webhookResponse = `{
  "success": 1,
  "data": ${webhook('  ')},
  "error": null
}`

const fields: string[] = [
  'В ответе пароль не возвращается: basic_auth_password — «Установлен» или «Не установлен».',
  'headers — объект «имя заголовка: значение» или null, если заголовки не заданы.',
]

const validationErrors: ApiDocBlock = {
  id: 'errors',
  heading: 'Ошибки',
  text: [
    '422 — ошибка проверки: в error — первая ошибка, в data — все ошибки по полям.',
    '404 — вебхука с таким id нет.',
    '401 — нет токена руководителя или он устарел.',
  ],
  code: `{
  "success": 0,
  "error": "<текст первой ошибки>",
  "data": {
    "url": ["<текст ошибки>"]
  }
}`,
}

const notFound: ApiDocBlock = {
  id: 'errors',
  heading: 'Ошибки',
  text: ['404 — вебхука с таким id нет. 401 — нет токена руководителя или он устарел.'],
  code: `{
  "success": 0,
  "error": "Не найдено",
  "data": null
}`,
}

export const webhookApiDocs: Record<string, ApiDocPage> = {
  createWebhook: {
    link: ['POST /api/dashboard/v1/chief/webhooks'],
    blocks: [
      {
        id: 'about',
        heading: 'Как работают вебхуки',
        nav: 'Как работают',
        text: [
          'Методы этого раздела — методы кабинета: их вызывает руководитель компании с токеном входа в кабинет (Authorization: Bearer).',
          'Вебхук — адрес вашего сервера, на который мы отправляем запрос, когда происходит событие (список — на странице «Список событий»). На одно событие можно завести несколько вебхуков; запрос уходит на каждый включённый.',
          'Если заданы логин и пароль, запрос идёт с Basic-авторизацией. Заголовки из headers добавляются к каждому запросу.',
          'Если ваш сервер на событие customer.created ответит кодом 200 и JSON с полем id, это значение сохранится как внешний id покупателя.',
        ],
      },
      {
        id: 'params',
        heading: 'Параметры',
        code: `{
  "event": "customer.created",              // обязательно; одно из значений event из «Списка событий»
  "method": "POST",                         // обязательно; GET, POST, PUT, PATCH или DELETE
  "url": "https://crm.example.com/ifb/hook", // обязательно; полный адрес
  "basic_auth_login": "crm",                // не обязательно; обязательно, если передан basic_auth_password
  "basic_auth_password": "secret",          // не обязательно; обязательно, если передан basic_auth_login
  "headers": {                              // не обязательно; объект «имя заголовка: значение»
    "X-Source": "ifb"
  },
  "is_active": true                         // не обязательно; true или false, по умолчанию true
}`,
      },
      {
        id: 'success',
        heading: 'Пример ответа',
        text: fields,
        code: webhookResponse,
      },
      validationErrors,
    ],
  },

  webhookEvents: {
    link: ['GET /api/dashboard/v1/chief/webhooks/events'],
    blocks: [
      {
        id: 'success',
        heading: 'Пример ответа',
        text: ['event — значение для поля event при создании вебхука, event_name — название события.'],
        code: `{
  "success": 1,
  "data": [
    { "event": "customer.created", "event_name": "Покупатель создан" },
    { "event": "customer.updated", "event_name": "Покупатель обновлён" },
    { "event": "customer.deleted", "event_name": "Покупатель удалён" },
    { "event": "customer.download", "event_name": "Выгрузка покупателей инициирована" },
    { "event": "bonus.income", "event_name": "Бонусы начислены" },
    { "event": "bonus.expense", "event_name": "Бонусы списаны" },
    { "event": "bonus.hold", "event_name": "Бонусы захолдированы" },
    { "event": "order.created", "event_name": "Заказ создан" },
    { "event": "order.updated", "event_name": "Заказ обновлён" },
    { "event": "order.download", "event_name": "Выгрузка заказов инициирована" }
  ],
  "error": null
}`,
      },
    ],
  },

  updateWebhook: {
    link: ['PUT /api/dashboard/v1/chief/webhooks/:id'],
    blocks: [
      {
        id: 'params',
        heading: 'Параметры',
        text: [
          'Все поля не обязательны: меняются только присланные. Присланные event, method и url не могут быть пустыми.',
          'Логин и пароль передаются только вместе. Чтобы убрать Basic-авторизацию, передайте оба поля со значением null.',
          'headers заменяют прежние заголовки целиком, а не добавляются к ним; null убирает все заголовки.',
          'is_active: false выключает вебхук — запросы на него не уходят, настройки сохраняются. true включает обратно.',
        ],
        code: `{
  "event": "customer.updated",              // одно из значений event из «Списка событий»
  "method": "PUT",                          // GET, POST, PUT, PATCH или DELETE
  "url": "https://crm.example.com/ifb/hook",
  "basic_auth_login": "crm",                // вместе с basic_auth_password
  "basic_auth_password": "new-secret",      // вместе с basic_auth_login
  "headers": {
    "X-Source": "ifb"
  },
  "is_active": false
}`,
      },
      {
        id: 'success',
        heading: 'Пример ответа',
        text: ['Ответ — вебхук целиком, как в «Показать вебхук».'],
        code: webhookResponse,
      },
      validationErrors,
    ],
  },

  getWebhook: {
    link: ['GET /api/dashboard/v1/chief/webhooks/:id'],
    blocks: [
      {
        id: 'success',
        heading: 'Пример ответа',
        text: [':id — id вебхука из ответа на создание или из списка.', ...fields],
        code: webhookResponse,
      },
      notFound,
    ],
  },

  webhookList: {
    link: ['GET /api/dashboard/v1/chief/webhooks'],
    blocks: [
      {
        id: 'params',
        heading: 'Параметры',
        text: [
          'Параметры строки запроса, оба не обязательны: page — номер страницы (по умолчанию 1), per_page — сколько вебхуков на странице (по умолчанию 15).',
          'Вебхуки отсортированы по событию.',
        ],
        code: 'GET /api/dashboard/v1/chief/webhooks?page=1&per_page=15',
      },
      {
        id: 'success',
        heading: 'Пример ответа',
        text: [
          'В data.data — вебхуки в том же виде, что в «Показать вебхук».',
          'pagination: total — всего вебхуков, total_pages — всего страниц.',
        ],
        code: `{
  "success": 1,
  "data": {
    "data": [
      ${webhook('      ')}
    ],
    "pagination": {
      "current_page": 1,
      "per_page": 15,
      "total": 1,
      "total_pages": 1
    }
  },
  "error": null
}`,
      },
    ],
  },

  deleteWebhook: {
    link: ['DELETE /api/dashboard/v1/chief/webhooks/:id'],
    blocks: [
      {
        id: 'success',
        heading: 'Пример ответа',
        text: [':id — id вебхука. Удаление окончательное; чтобы временно остановить отправку, выключите вебхук (is_active: false).'],
        code: `{
  "success": 1,
  "data": {
    "message": "ok"
  },
  "error": null
}`,
      },
      notFound,
    ],
  },
}
